import React from 'react';
import styled from 'styled-components';
import { isNil } from 'lodash-es';
import { Icon, IconLookup } from 'component-library-and-tools';

import List from 'shared/components/List';
import { TransformationType } from 'shared/types/transformation';
import { getReadableTransformationType } from 'shared/utils/transformation';

import FlowLabeledContent from '../FlowLabeledContent';
import WorkflowFlowBox from '../WorkflowFlowBox';

import { TransformationValueWrapper } from './TransformationValueWrapper';
import { WorkflowTransformationBoxProps } from './types';

const TRANSFORMATION_LIST_ITEM_WRAPPER = 'transformation-list__item-wrapper';

const StyledTransformationListContainer = styled.div``;

const StyledTransformationList = styled(List)`
  & {
    :not(:last-child) {
      margin-bottom: 1.5rem;
    }

    .${TRANSFORMATION_LIST_ITEM_WRAPPER}:not(:last-child) {
      margin-bottom: 0.916666667rem;
    }
  }
`;

const StyledTransformationListTitleWrapper = styled.div`
  margin-bottom: 0.666666667rem;
`;

const StyledTransformationTitle = styled.span`
  color: #8d9199;
  font-size: 1rem;
`;

const StyledTransformationTitleIcon = styled(Icon)`
  margin-right: 0.333333333rem;
  color: #a9aeb8;
`;

const StyledTransformationQuantityWrapper = styled.div`
  display: inline-flex;
  align-items: center;

  &:not(:last-child) {
    margin-right: 1.666666667rem;
  }
`;

const StyledTransformationQuantityIcon = styled(Icon)`
  && {
    margin-right: 0.5rem;
    color: #a9aeb8;
    font-size: 1.666666667rem;
  }
`;

const StyledTransformationQuantity = styled.span`
  color: #676a70;
  font-size: 1rem;
  font-weight: 700;
`;

// Maps a transformation type to an icon.
const transformationIconMapper = {
  [TransformationType.INDICATOR_TYPE]: IconLookup.SHIELD1,
  [TransformationType.SCORE_FILTERING]: IconLookup.ALIGN_CENTER_VERTICAL,
  [TransformationType.SAFELIST_LIBRARY]: IconLookup.TEXT_ALIGN_JUSTIFY,
};

const getTransformationIcon = (transformationType: TransformationType) => (
  transformationIconMapper[transformationType]
);

const renderTransformationLists = (
  transformations: WorkflowTransformationBoxProps['transformations'],
) => (
  <StyledTransformationListContainer>
    {Object.entries(transformations).map(([transformationType, transformationValues]) => {
      const castType = transformationType as TransformationType;

      const title = (
        <StyledTransformationListTitleWrapper>
          <StyledTransformationTitleIcon icon={getTransformationIcon(castType)} />
          <StyledTransformationTitle>{getReadableTransformationType(castType)}</StyledTransformationTitle>
        </StyledTransformationListTitleWrapper>
      );

      return (
        <StyledTransformationList
          key={castType}
          title={title}
          items={transformationValues || []}
          itemWrapperClassName={TRANSFORMATION_LIST_ITEM_WRAPPER}
          renderItem={(value) => (
            <TransformationValueWrapper
              type={castType}
              value={value}
            />
          )}
        />
      );
    })}
  </StyledTransformationListContainer>
);

const renderTransformationQuantities = (
  transformations: WorkflowTransformationBoxProps['transformations'],
) => (
  <FlowLabeledContent label='Transformations'>
    {Object.entries(transformations).map(([transformationType, transformationValues]) => {
      if (isNil(transformationValues)) {
        return null;
      }

      return (
        <StyledTransformationQuantityWrapper>
          <StyledTransformationQuantityIcon icon={getTransformationIcon(transformationType as TransformationType)} />
          <StyledTransformationQuantity>{transformationValues.length}</StyledTransformationQuantity>
        </StyledTransformationQuantityWrapper>
      );
    })}
  </FlowLabeledContent>
);

const TransformationFlowBox = ({
  isOpen,
  transformations,
}: WorkflowTransformationBoxProps) => (
  <WorkflowFlowBox
    isOpen={isOpen}
    contentWhenIsOpen={renderTransformationLists(transformations)}
    contentWhenIsClosed={renderTransformationQuantities(transformations)}
  />
);

export default TransformationFlowBox;
